import React, { useState, useEffect } from 'react';
import { Plus, Edit, Trash2, Save, X, Upload, Loader2, CheckSquare, Square } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Card } from '../../components/ui/card';
import { Progress } from '../../components/ui/progress';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../../components/ui/dialog';
import { recitationAPI, reciterAPI, surahAPI } from '../../services/api';
import { useToast } from '../../hooks/use-toast';
import { getErrorMessage } from '../../utils/errorHandler';

const RecitationManagement = () => {
  const { toast } = useToast();
  const [recitations, setRecitations] = useState([]);
  const [reciters, setReciters] = useState([]);
  const [surahs, setSurahs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingRecitation, setEditingRecitation] = useState(null);
  const [formData, setFormData] = useState({
    reciterId: '',
    surahId: '',
    order: 1
  });
  const [audioFile, setAudioFile] = useState(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadController, setUploadController] = useState(null);
  const [selectedRecitations, setSelectedRecitations] = useState([]);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [recitationsRes, recitersRes, surahsRes] = await Promise.all([
        reciterAPI.getAll().then(res => {
          // Get all recitations from all reciters
          const promises = res.data.map(r => 
            recitationAPI.getByReciter(r.id).catch(() => ({ data: [] }))
          );
          return Promise.all(promises).then(results => {
            const allRecitations = results.flatMap(r => r.data);
            return { data: allRecitations };
          });
        }),
        reciterAPI.getAll(),
        surahAPI.getAll()
      ]);
      
      setRecitations(recitationsRes.data);
      setReciters(recitersRes.data);
      setSurahs(surahsRes.data);
    } catch (error) {
      console.error('Error loading data:', error);
      toast({
        title: 'Hata!',
        description: 'Veriler yuklenirken hata olustu.',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (recitation = null) => {
    if (recitation) {
      setEditingRecitation(recitation);
      setFormData({
        reciterId: recitation.reciterId,
        surahId: recitation.surahId,
        order: recitation.order
      });
    } else {
      setEditingRecitation(null);
      setFormData({
        reciterId: '',
        surahId: '',
        order: recitations.length + 1
      });
    }
    setAudioFile(null);
    setIsDialogOpen(true);
  };

  const handleCloseDialog = () => {
    // Cancel upload if in progress
    if (uploadController) {
      uploadController.abort();
    }
    setIsDialogOpen(false);
    setEditingRecitation(null);
    setAudioFile(null);
    setIsUploading(false);
    setUploadProgress(0);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: name === 'order' ? parseInt(value) : value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    setIsUploading(true);
    setUploadProgress(0);
    
    try {
      const formDataToSend = new FormData();
      formDataToSend.append('reciterId', formData.reciterId);
      formDataToSend.append('surahId', formData.surahId);
      formDataToSend.append('order', formData.order);

      if (audioFile) {
        formDataToSend.append('audioFile', audioFile);
      }

      // Progress callback
      const onProgress = (progress) => {
        setUploadProgress(progress);
      };

      if (editingRecitation) {
        await recitationAPI.update(editingRecitation.id, formDataToSend, onProgress);
        toast({
          title: '✅ Basarili!',
          description: `Tilavet basariyla guncellendi. Sure otomatik olarak hesaplandi: ${audioFile ? audioFile.name : 'Dosya yuklenemedi'}`,
          duration: 5000
        });
      } else {
        await recitationAPI.create(formDataToSend, onProgress);
        toast({
          title: '✅ Basarili!',
          description: `Tilavet basariyla eklendi! Sure otomatik olarak hesaplandi. Dosya: ${audioFile.name}`,
          duration: 5000
        });
      }
      
      handleCloseDialog();
      loadData();
    } catch (error) {
      console.error('Error saving recitation:', error);
      toast({
        title: '❌ Hata!',
        description: getErrorMessage(error, 'Tilavet kaydedilirken hata olustu.'),
        variant: 'destructive',
        duration: 7000
      });
    } finally {
      setIsUploading(false);
      setUploadProgress(0);
    }
  };

  const handleDelete = async (id) => {
    setIsDeleteDialogOpen(false);
    
    try {
      await recitationAPI.delete(id);
      toast({
        title: '✅ Başarılı!',
        description: 'Tilavet başarıyla silindi.',
        className: 'bg-green-50 border-green-200'
      });
      loadData();
    } catch (error) {
      console.error('Error deleting recitation:', error);
      toast({
        title: '❌ Hata!',
        description: 'Tilavet silinirken hata oluştu.',
        variant: 'destructive'
      });
    }
  };

  const toggleSelectRecitation = (id) => {
    setSelectedRecitations(prev =>
      prev.includes(id) ? prev.filter(rId => rId !== id) : [...prev, id]
    );
  };

  const toggleSelectAll = () => {
    if (selectedRecitations.length === recitations.length) {
      setSelectedRecitations([]);
    } else {
      setSelectedRecitations(recitations.map(r => r.id));
    }
  };

  const handleBulkDelete = async () => {
    if (selectedRecitations.length === 0) return;
    setIsDeleteDialogOpen(false);
    
    try {
      await Promise.all(selectedRecitations.map(id => recitationAPI.delete(id)));
      toast({
        title: '✅ Başarılı!',
        description: `${selectedRecitations.length} tilavet başarıyla silindi.`,
        className: 'bg-green-50 border-green-200'
      });
      setSelectedRecitations([]);
      loadData();
    } catch (error) {
      console.error('Error bulk deleting recitations:', error);
      toast({
        title: '❌ Hata!',
        description: 'Tilavetler silinirken hata oluştu.',
        variant: 'destructive'
      });
    }
  };

  const getReciterName = (reciterId) => {
    const reciter = reciters.find(r => r.id === reciterId);
    return reciter ? reciter.name : 'Bilinmeyen';
  };

  const getSurahName = (surahId) => {
    const surah = surahs.find(s => s.id === surahId);
    return surah ? surah.name : 'Bilinmeyen';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <p className="text-[#8B7355]">Yukleniyor...</p>
      </div>
    );
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center space-x-4">
          <h2 className="text-2xl font-bold text-[#5C4033]">Tilavet Yönetimi</h2>
          {selectedRecitations.length > 0 && (
            <Button
              onClick={() => setIsDeleteDialogOpen(true)}
              variant="destructive"
              size="sm"
              className="bg-red-600 hover:bg-red-700"
            >
              <Trash2 className="h-4 w-4 mr-2" />
              Seçilenleri Sil ({selectedRecitations.length})
            </Button>
          )}
        </div>
        <Button
          onClick={() => handleOpenDialog()}
          className="bg-[#8B7355] hover:bg-[#6B5744] text-white"
        >
          <Plus className="h-4 w-4 mr-2" />
          Yeni Tilavet Ekle
        </Button>
      </div>

      {recitations.length > 0 && (
        <div className="mb-4">
          <Button
            onClick={toggleSelectAll}
            variant="outline"
            size="sm"
            className="border-[#D5C4A1] text-[#5C4033]"
          >
            {selectedRecitations.length === recitations.length ? (
              <CheckSquare className="h-4 w-4 mr-2" />
            ) : (
              <Square className="h-4 w-4 mr-2" />
            )}
            {selectedRecitations.length === recitations.length ? 'Tümünü Kaldır' : 'Tümünü Seç'}
          </Button>
        </div>
      )}

      <div className="space-y-6">
        {/* Hafızlara göre grupla */}
        {reciters.map((reciter) => {
          const reciterRecitations = recitations.filter(r => r.reciterId === reciter.id);
          
          // Eğer bu hafızın hiç tilaveti yoksa gösterme
          if (reciterRecitations.length === 0) return null;
          
          return (
            <div key={reciter.id} className="border-2 border-[#D5C4A1] rounded-lg p-4 bg-[#FDFBF7]">
              {/* Hafız Başlığı */}
              <div className="flex items-center justify-between mb-4 pb-3 border-b-2 border-[#E8DCC8]">
                <div className="flex items-center space-x-3">
                  {reciter.image && (
                    <img 
                      src={reciter.image.startsWith('http') ? reciter.image : `${process.env.REACT_APP_BACKEND_URL}/api/files/${reciter.image.replace('/uploads/', '')}`}
                      alt={reciter.name}
                      className="w-12 h-12 rounded-full object-cover border-2 border-[#8B7355]"
                    />
                  )}
                  <div>
                    <h2 className="text-xl font-bold text-[#5C4033]">{reciter.name}</h2>
                    <p className="text-sm text-[#8B7355]">{reciterRecitations.length} tilavet</p>
                  </div>
                </div>
                <div className="bg-[#8B7355] text-white px-3 py-1 rounded-full text-sm font-semibold">
                  {reciterRecitations.length} Sure
                </div>
              </div>
              
              {/* Bu hafızın tilavetleri */}
              <div className="space-y-2">
                {reciterRecitations.map((recitation) => (
                  <Card key={recitation.id} className="bg-white border-[#E8DCC8] p-3 hover:shadow-md transition-shadow">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-3">
                        <button
                          onClick={() => toggleSelectRecitation(recitation.id)}
                          className="flex-shrink-0"
                        >
                          {selectedRecitations.includes(recitation.id) ? (
                            <CheckSquare className="h-5 w-5 text-[#8B7355]" />
                          ) : (
                            <Square className="h-5 w-5 text-[#D5C4A1]" />
                          )}
                        </button>
                        <div className="flex-shrink-0 w-8 h-8 bg-[#D5C4A1] rounded-full flex items-center justify-center text-[#5C4033] font-bold text-sm">
                          {recitation.order}
                        </div>

                        <div className="flex-1">
                          <h3 className="font-semibold text-[#3E3028]">
                            {getSurahName(recitation.surahId)}
                          </h3>
                          <p className="text-xs text-[#8B7355]">
                            Süre: {recitation.duration}
                          </p>
                        </div>
                      </div>

                      <div className="flex items-center space-x-1">
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleOpenDialog(recitation)}
                          className="text-[#8B7355] hover:bg-[#F5EFE7] h-8 w-8"
                        >
                          <Edit className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleDelete(recitation.id)}
                          className="text-red-600 hover:bg-red-50 h-8 w-8"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  </Card>
                ))}
              </div>
            </div>
          );
        })}
      </div>

      {recitations.length === 0 && (
        <Card className="bg-white border-[#E8DCC8] p-12">
          <div className="text-center">
            <p className="text-[#8B7355]">
              Henuz tilavet eklenmemis. Yeni tilavet eklemek icin yukardaki butona tiklayin.
            </p>
          </div>
        </Card>
      )}

      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="max-w-2xl bg-[#F9F6F1]">
          <DialogHeader>
            <DialogTitle className="text-2xl font-bold text-[#5C4033]">
              {editingRecitation ? 'Tilavet Duzenle' : 'Yeni Tilavet Ekle'}
            </DialogTitle>
            <DialogDescription className="sr-only">
              Tilavet ekleme veya düzenleme formu. Büyük ses dosyaları desteklenir.
            </DialogDescription>
          </DialogHeader>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <label className="text-sm font-medium text-[#5C4033]">Hafiz</label>
              <select
                name="reciterId"
                value={formData.reciterId}
                onChange={handleChange}
                required
                className="w-full px-3 py-2 border border-[#D5C4A1] rounded-md bg-white text-[#5C4033]"
              >
                <option value="">Hafiz Sec...</option>
                {reciters.map(reciter => (
                  <option key={reciter.id} value={reciter.id}>
                    {reciter.name}
                  </option>
                ))}
              </select>
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium text-[#5C4033]">Sure</label>
              <select
                name="surahId"
                value={formData.surahId}
                onChange={handleChange}
                required
                className="w-full px-3 py-2 border border-[#D5C4A1] rounded-md bg-white text-[#5C4033]"
              >
                <option value="">Sure Sec...</option>
                {surahs.map(surah => (
                  <option key={surah.id} value={surah.id}>
                    {surah.name}
                  </option>
                ))}
              </select>
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium text-[#5C4033]">Sira Numarasi</label>
              <Input
                name="order"
                type="number"
                value={formData.order}
                onChange={handleChange}
                required
                min="1"
                className="bg-white border-[#D5C4A1]"
              />
              <p className="text-xs text-[#8B7355]">
                ℹ️ Sure otomatik olarak ses dosyasindan hesaplanacaktir.
              </p>
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium text-[#5C4033]">Ses Dosyası (MP3 veya MP4)</label>
              <div className="flex items-center space-x-2">
                <Input
                  type="file"
                  accept=".mp3,.mp4,audio/mpeg,audio/mp4"
                  onChange={(e) => {
                    const file = e.target.files[0];
                    if (file) {
                      // Check file extension
                      const fileName = file.name.toLowerCase();
                      if (!fileName.endsWith('.mp3') && !fileName.endsWith('.mp4')) {
                        toast({
                          title: '⚠️ Uyarı!',
                          description: 'Sadece MP3 ve MP4 dosyaları kabul edilmektedir. Lütfen dosyanızı uygun formata dönüştürün.',
                          variant: 'destructive',
                          duration: 7000
                        });
                        e.target.value = ''; // Clear input
                        return;
                      }
                      setAudioFile(file);
                    }
                  }}
                  required={!editingRecitation}
                  className="bg-white border-[#D5C4A1]"
                />
                <Upload className="h-5 w-5 text-[#8B7355]" />
              </div>
              <div className="bg-amber-50 border border-amber-200 rounded-md p-2 mt-2">
                <p className="text-xs text-amber-700 font-medium">
                  ✅ Desteklenen formatlar: MP3, MP4
                </p>
                <p className="text-xs text-amber-600 mt-1">
                  M4A, WAV, OGG gibi diğer formatlar tarayıcılarda sorun çıkarabilir.
                </p>
              </div>
              {editingRecitation && !audioFile && (
                <p className="text-xs text-[#8B7355]">Mevcut dosyayı değiştirmek için yeni bir dosya seçin.</p>
              )}
            </div>

            {/* Upload Progress */}
            {isUploading && (
              <div className="space-y-2 pt-4">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-[#8B7355]">Yukleniyor...</span>
                  <span className="text-[#5C4033] font-semibold">{uploadProgress}%</span>
                </div>
                <Progress value={uploadProgress} className="h-3" />
                <p className="text-xs text-[#8B7355] text-center">
                  {uploadProgress < 30 && '📤 Dosya yukleniyor...'}
                  {uploadProgress >= 30 && uploadProgress < 70 && '⏱️ Sure hesaplaniyor...'}
                  {uploadProgress >= 70 && uploadProgress < 100 && '💾 Kaydediliyor...'}
                  {uploadProgress === 100 && '✅ Tamamlandi!'}
                </p>
              </div>
            )}

            <div className="flex justify-end space-x-3 pt-4">
              <Button
                type="button"
                variant="ghost"
                onClick={handleCloseDialog}
                className="text-[#6B5744] hover:bg-[#E8DCC8]"
              >
                <X className="h-4 w-4 mr-2" />
                {isUploading ? 'İptal Et' : 'Kapat'}
              </Button>
              <Button
                type="submit"
                disabled={isUploading}
                className="bg-[#8B7355] hover:bg-[#6B5744] text-white"
              >
                {isUploading ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Yukleniyor...
                  </>
                ) : (
                  <>
                    <Save className="h-4 w-4 mr-2" />
                    {editingRecitation ? 'Guncelle' : 'Kaydet'}
                  </>
                )}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
        <DialogContent className="bg-white max-w-md">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold text-[#5C4033]">Silme Onayı</DialogTitle>
            <DialogDescription className="text-[#8B7355] mt-2">
              {selectedRecitations.length > 0
                ? `${selectedRecitations.length} tilaveti silmek istediğinizden emin misiniz? Bu işlem geri alınamaz.`
                : 'Bu tilaveti silmek istediğinizden emin misiniz?'}
            </DialogDescription>
          </DialogHeader>
          <div className="flex justify-end space-x-3 mt-6">
            <Button
              type="button"
              variant="outline"
              onClick={() => setIsDeleteDialogOpen(false)}
              className="border-[#D5C4A1] text-[#5C4033] hover:bg-[#F5EFE7]"
            >
              İptal
            </Button>
            <Button
              onClick={handleBulkDelete}
              className="bg-red-600 hover:bg-red-700 text-white"
            >
              <Trash2 className="h-4 w-4 mr-2" />
              Evet, Sil
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default RecitationManagement;
